import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { applyNodeChanges, Background, Controls, Node, NodeChange, ReactFlow, ReactFlowProvider } from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import "../../../i18/config";
import {
    activateWorkflowScenario,
    deactivateWorkflowScenario,
    exportWorkflowScenario,
    getWorkflowExecution,
    getWorkflowScenario,
    restoreWorkflowScenarioVersion,
    runWorkflowScenario,
    saveWorkflowScenario,
    saveWorkflowTemplate,
} from "../../../api";
import * as route from "../../../api/routes";
import Loader from "../../../components/Loader";
import { Blueprint, BlueprintModule, ModuleDefinition, ValidationReport, WorkflowExecution, WorkflowFilter, WorkflowScenario, WorkflowSchedule, WorkflowSettings } from "../types";
import { useModuleCatalog } from "../useModuleCatalog";
import { useLayoutMode } from "../../../hooks/useLayoutMode";
import {
    addRoute,
    edges as blueprintEdges,
    findModule,
    insertModule,
    layout,
    moveModule,
    nextId,
    Placement,
    removeModule,
    removeRoute,
    ROUTER,
    GAP_X,
    updateModule,
} from "./blueprint";
import ModuleNode, { ModuleFlowNode, ModuleRunSummary } from "./ModuleNode";
import AddNode, { AddFlowNode } from "./AddNode";
import WorkflowEdge, { WorkflowFlowEdge } from "./WorkflowEdge";
import ModulePanel from "./ModulePanel";
import ModulePicker from "./ModulePicker";
import FilterEditor from "./FilterEditor";
import ScheduleModal from "./ScheduleModal";
import SettingsModal from "./SettingsModal";
import VersionsModal from "./VersionsModal";
import BundlesModal from "./BundlesModal";
import { MappingProvider } from "./MappingInput";
import { ScenarioStatusBadge, ExecutionStatusBadge } from "../StatusBadge";
import { describeSchedule, isFinalExecution } from "../format";
import { NameModal } from "../ui/Dialogs";

const nodeTypes = { module: ModuleNode, add: AddNode };
const edgeTypes = { workflow: WorkflowEdge };
const POLL_MS = 1500;

const emptyScenario = (name: string): WorkflowScenario => ({
    uuid: '', name, status: 'draft', version: 0, isInstant: false, settings: {}, consecutiveErrors: 0, blueprint: { flow: [] },
});

/** Initial values from a module's schema defaults. */
const defaults = (fields: ModuleDefinition['parameters']) =>
    Object.fromEntries(fields.filter((f) => f.default !== undefined && f.default !== null).map((f) => [f.name, f.default]));

/** Downloads `data` as a JSON file. */
const download = (name: string, data: any) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = name.replace(/[^\w\-. ]+/g, '_') + '.blueprint.json';
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
};

const ScenarioEditorInner = () => {
    const { t } = useTranslation(['workflow', 'common']);
    const layoutMode = useLayoutMode();
    const { uuid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const catalog = useModuleCatalog();
    const isNew = !uuid || uuid === 'new';

    const [scenario, setScenario] = useState<WorkflowScenario | null>(null);
    const [blueprint, setBlueprint] = useState<Blueprint>({ flow: [] });
    const [name, setName] = useState('');
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [report, setReport] = useState<ValidationReport | null>(null);
    const [selected, setSelected] = useState<number | null>(null);
    const [picker, setPicker] = useState<Placement | null>(null);
    const [filterOf, setFilterOf] = useState<number | null>(null);
    const [modal, setModal] = useState<'schedule' | 'settings' | 'versions' | null>(null);
    const [templateNaming, setTemplateNaming] = useState(false);
    const [execution, setExecution] = useState<WorkflowExecution | null>(null);
    const [bundlesOf, setBundlesOf] = useState<number | null>(null);
    // The canvas nodes: rebuilt from the blueprint, with xyflow's own changes
    // (measured sizes, drag positions) applied on top.
    const [nodes, setNodes] = useState<Node[]>([]);
    const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const readOnly = !isNew && scenario !== null && scenario.actions?.canEdit === false;

    // -- Loading ------------------------------------------------------------

    const load = useCallback(() => {
        if (isNew) {
            const fresh = emptyScenario(t('workflow:newScenario'));
            setScenario(fresh);
            setBlueprint({ flow: [] });
            setName(fresh.name);

            return;
        }
        getWorkflowScenario(uuid as string)
            .then((response: any) => {
                const item: WorkflowScenario = response.item;
                setScenario(item);
                setBlueprint(item.blueprint || { flow: [] });
                setName(item.name);
                setDirty(false);
            })
            .catch(() => {
                toast.error(t('workflow:notFound'));
                navigate(route.AUTOMATION_SCENARIOS);
            });
    }, [isNew, uuid, navigate, t]);

    useEffect(() => load(), [load]);

    // A run to show on the canvas: ?run=<execution uuid> (from the history).
    const watchExecution = useCallback((executionUuid: string) => {
        if (pollTimer.current) {
            clearTimeout(pollTimer.current);
        }
        const tick = () => {
            getWorkflowExecution(executionUuid)
                .then((response: any) => {
                    const item: WorkflowExecution = response.item;
                    setExecution(item);
                    if (!isFinalExecution(item.status) && item.status !== 'waiting') {
                        pollTimer.current = setTimeout(tick, POLL_MS);
                    }
                })
                .catch(() => undefined);
        };
        tick();
    }, []);

    useEffect(() => {
        const run = new URLSearchParams(location.search).get('run');
        if (run) {
            watchExecution(run);
        }

        return () => {
            if (pollTimer.current) {
                clearTimeout(pollTimer.current);
            }
        };
    }, [location.search, watchExecution]);

    // -- Editing --------------------------------------------------------------

    const change = useCallback((next: Blueprint) => {
        setBlueprint(next);
        setDirty(true);
    }, []);

    const kindOf = useCallback((key: string) => (key === ROUTER ? 'router' : catalog?.byKey[key]?.kind === 'directive' ? 'directive' : null), [catalog]);

    const addModule = (definition: ModuleDefinition) => {
        if (!picker) {
            return;
        }
        const id = nextId(blueprint);
        const anchor = picker.kind === 'after' ? findModule(blueprint, picker.moduleId)?.metadata?.designer : undefined;
        const module: BlueprintModule = {
            id,
            module: definition.key,
            version: definition.version,
            parameters: defaults(definition.parameters),
            mapper: definition.kind === 'router' ? null : defaults(definition.mapper),
            metadata: anchor ? { designer: { x: anchor.x + GAP_X, y: anchor.y } } : {},
        };
        if (definition.kind === 'router') {
            module.routes = [{ flow: [] }, { flow: [] }];
        }
        change(insertModule(blueprint, picker, module));
        setPicker(null);
        setSelected(definition.kind === 'router' ? null : id);
    };

    const onNodesChange = useCallback((changes: NodeChange[]) => {
        setNodes((current) => applyNodeChanges(changes, current));
    }, []);

    const onNodeDragStop = useCallback((_: any, node: any) => {
        if (node.type === 'module') {
            const id = parseInt(String(node.id).slice(1), 10);
            setBlueprint((current) => moveModule(current, id, node.position));
            setDirty(true);
        }
    }, []);

    const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
        if (node.type === 'add') {
            setPicker((node.data as any).placement);

            return;
        }
        const id = parseInt(String(node.id).slice(1), 10);
        if ((event.target as HTMLElement).closest('[data-run]')) {
            setBundlesOf(id);
        } else {
            setSelected(id);
        }
    }, []);

    // -- Saving, running, status ---------------------------------------------

    const save = async (extra: Record<string, any> = {}): Promise<WorkflowScenario | null> => {
        setSaving(true);
        try {
            const response: any = await saveWorkflowScenario({
                ...(isNew ? {} : { uuid }),
                name,
                blueprint,
                ...extra,
            });
            setReport(response.validation || null);
            setDirty(false);
            toast.success(t('workflow:saved'));
            if (isNew) {
                navigate(route.AUTOMATION_SCENARIO.replace(':uuid', response.uuid), { replace: true });

                return null;
            }
            const fresh: any = await getWorkflowScenario(uuid as string);
            setScenario(fresh.item);

            return fresh.item;
        } catch (error: any) {
            if (error?.validation) {
                setReport(error.validation);
            }
            toast.error(error?.message || t('workflow:saveFailed'));

            return null;
        } finally {
            setSaving(false);
        }
    };

    const runOnce = async () => {
        if (isNew || dirty) {
            const saved = await save();
            if (!saved) {
                return;
            }
        }
        try {
            const response: any = await runWorkflowScenario(uuid as string);
            toast.info(t('workflow:runStarted'));
            watchExecution(response.execution);
        } catch (error: any) {
            if (error?.validation) {
                setReport(error.validation);
            }
            toast.error(error?.message || t('workflow:runFailed'));
        }
    };

    const toggleActive = async () => {
        if (!scenario || isNew) {
            return;
        }
        try {
            const response: any = scenario.status === 'active'
                ? await deactivateWorkflowScenario(scenario.uuid)
                : await activateWorkflowScenario(scenario.uuid);
            setScenario({ ...scenario, ...response.item });
            toast.success(response.message || t('workflow:saved'));
        } catch (error: any) {
            if (error?.validation) {
                setReport(error.validation);
            }
            toast.error(error?.message || t('workflow:activationFailed'));
        }
    };

    const exportBlueprint = async () => {
        if (!scenario || isNew) {
            download(name, { ...blueprint, name });

            return;
        }
        const response: any = await exportWorkflowScenario(scenario.uuid);
        download(name, response.blueprint);
    };

    const restore = async (version: number) => {
        if (!scenario) {
            return;
        }
        try {
            await restoreWorkflowScenarioVersion(scenario.uuid, version);
            setModal(null);
            load();
            toast.success(t('workflow:restored', { version }));
        } catch (error: any) {
            toast.error(error?.message || t('workflow:saveFailed'));
        }
    };

    const saveAsTemplate = async (templateName: string) => {
        if (!scenario || isNew) {
            return;
        }
        try {
            await saveWorkflowTemplate({ name: templateName, description: scenario.description || null, scenario: scenario.uuid });
            toast.success(t('workflow:templateSaved'));
            setTemplateNaming(false);
        } catch (error: any) {
            toast.error(error?.message || t('workflow:saveFailed'));
        }
    };

    // -- Canvas ---------------------------------------------------------------

    const runByModule = useMemo(() => {
        const summary: Record<number, ModuleRunSummary> = {};
        (execution?.steps || []).forEach((step) => {
            const entry = summary[step.moduleId] || { operations: 0, status: 'done', steps: [] };
            entry.operations++;
            entry.steps.push(step);
            const rank = { done: 0, handled: 1, running: 2, waiting: 3, error: 4 };
            if (rank[step.status] > rank[entry.status]) {
                entry.status = step.status;
            }
            summary[step.moduleId] = entry;
        });

        return summary;
    }, [execution]);

    const samples = useMemo(() => {
        const result: Record<number, any> = {};
        Object.entries(runByModule).forEach(([id, summary]) => {
            const bundle = summary.steps.find((s) => (s.output || []).length > 0)?.output?.[0];
            if (bundle !== undefined) {
                result[Number(id)] = bundle;
            }
        });

        return result;
    }, [runByModule]);

    const problemsOf = useCallback((id: number) => [
        ...(report?.errors || []).filter((e) => e.moduleId === id).map((e) => ({ message: e.message, level: 'error' as const })),
        ...(report?.warnings || []).filter((e) => e.moduleId === id).map((e) => ({ message: e.message, level: 'warning' as const })),
    ], [report]);

    const graph = useMemo(() => {
        if (!catalog) {
            return { nodes: [] as Array<ModuleFlowNode | AddFlowNode>, edges: [] as WorkflowFlowEdge[] };
        }
        const { positions, slots } = layout(blueprint, kindOf);
        const nodes: Array<ModuleFlowNode | AddFlowNode> = [];

        Object.entries(positions).forEach(([idText, position]) => {
            const id = Number(idText);
            const module = findModule(blueprint, id) as BlueprintModule;
            const nodeId = 'm' + id;
            nodes.push({
                id: nodeId,
                type: 'module',
                position,
                selected: selected === id,
                data: {
                    module,
                    definition: catalog.byKey[module.module],
                    hasError: (report?.errors || []).some((e) => e.moduleId === id),
                    hasWarning: (report?.warnings || []).some((e) => e.moduleId === id),
                    run: runByModule[id],
                },
            });
        });

        if (!readOnly) {
            slots.forEach((slot) => {
                nodes.push({
                    id: slot.key,
                    type: 'add',
                    position: slot.position,
                    draggable: false,
                    data: {
                        placement: slot.placement,
                        label: slot.placement.kind === 'start' ? t('workflow:addFirstModule') : undefined,
                    },
                });
            });
        }

        const edges: WorkflowFlowEdge[] = blueprintEdges(blueprint, readOnly ? [] : slots).map((e) => {
            const target = typeof e.target === 'number' ? 'm' + e.target : e.target;
            const filter = e.filterOf !== null ? findModule(blueprint, e.filterOf)?.filter : null;
            const routerId = e.kind === 'route' || (e.kind === 'slot' && e.routeIndex !== undefined) ? e.source : undefined;

            return {
                id: e.id,
                source: 'm' + e.source,
                target,
                type: 'workflow',
                data: {
                    kind: e.kind,
                    filterOf: e.filterOf,
                    hasFilter: !!filter && (filter.conditions || []).length > 0,
                    filterName: filter?.name,
                    routeIndex: e.routeIndex,
                    routerId,
                    onFilter: setFilterOf,
                    onRemoveRoute: readOnly ? undefined : (router: number, index: number) => change(removeRoute(blueprint, router, index)),
                },
            };
        });

        return { nodes, edges };
    }, [catalog, blueprint, kindOf, selected, report, runByModule, readOnly, t, change]);

    // Keep what xyflow measured when the blueprint-derived nodes are rebuilt.
    useEffect(() => {
        setNodes((current) => {
            const previous = new Map(current.map((n) => [n.id, n]));

            return graph.nodes.map((n) => {
                const old = previous.get(n.id);

                return old?.measured ? { ...n, measured: old.measured } : n;
            });
        });
    }, [graph.nodes]);

    if (!catalog || !scenario) {
        return <Loader />;
    }

    const selectedModule = selected !== null ? findModule(blueprint, selected) : undefined;
    const generalErrors = (report?.errors || []).filter((e) => e.moduleId === undefined);

    return (
        <div className="d-flex flex-column" style={{ height: 'calc(100vh - 140px)' }}>
            {/* Toolbar */}
            <div className="card mb-2">
            <div className="card-body py-2 d-flex flex-wrap align-items-center gap-2">
                <Link to={route.AUTOMATION_SCENARIOS} className="btn btn-sm btn-soft-secondary btn-icon" title={t('menu:automationScenarios')}><i className="ri-arrow-left-line" /></Link>
                <input className="form-control form-control-sm fw-medium fs-14" style={{ maxWidth: 320 }} value={name} disabled={readOnly}
                    onChange={(e) => { setName(e.target.value); setDirty(true); }} />
                <ScenarioStatusBadge status={scenario.status} />
                {!isNew &&
                    <button type="button" className="btn btn-ghost-primary btn-sm" onClick={() => setModal('schedule')} title={t('workflow:schedule')}>
                        <i className="ri-time-line align-bottom me-1" />{describeSchedule(scenario.schedule, t)}
                    </button>
                }
                <div className="ms-auto d-flex flex-wrap gap-2">
                    {!readOnly &&
                        <button type="button" className="btn btn-sm btn-primary" disabled={saving || (!dirty && !isNew)} onClick={() => save()}>
                            <i className="ri-save-line align-bottom me-1" />{t('workflow:save')}{dirty ? ' •' : ''}
                        </button>
                    }
                    {!readOnly &&
                        <button type="button" className="btn btn-sm btn-success" disabled={saving || blueprint.flow.length === 0} onClick={runOnce}>
                            <i className="ri-play-line align-bottom me-1" />{t('workflow:runOnce')}
                        </button>
                    }
                    {!isNew && !readOnly &&
                        <button type="button" className={"btn btn-sm " + (scenario.status === 'active' ? 'btn-warning' : 'btn-soft-success')} disabled={dirty} title={dirty ? t('workflow:saveFirst') : ''} onClick={toggleActive}>
                            <i className={(scenario.status === 'active' ? 'ri-pause-line' : 'ri-flashlight-line') + " align-bottom me-1"} />
                            {scenario.status === 'active' ? t('workflow:deactivate') : t('workflow:activate')}
                        </button>
                    }
                    <div className="btn-group btn-group-sm">
                        {!isNew && <button type="button" className="btn btn-soft-secondary" onClick={() => setModal('settings')} title={t('workflow:scenarioSettings')}><i className="ri-settings-3-line" /></button>}
                        {!isNew && <button type="button" className="btn btn-soft-secondary" onClick={() => setModal('versions')} title={t('workflow:versions')}><i className="ri-history-line" /></button>}
                        {!isNew && <Link to={route.AUTOMATION_RUNS + '?scenario=' + scenario.uuid} className="btn btn-soft-secondary" title={t('workflow:history')}><i className="ri-file-list-3-line" /></Link>}
                        {!isNew && !readOnly && <button type="button" className="btn btn-soft-secondary" onClick={() => setTemplateNaming(true)} title={t('workflow:saveAsTemplate')}><i className="ri-bookmark-line" /></button>}
                        <button type="button" className="btn btn-soft-secondary" onClick={exportBlueprint} title={t('workflow:export')}><i className="ri-download-2-line" /></button>
                    </div>
                </div>
            </div>
            </div>

            {generalErrors.length > 0 &&
                <div className="alert alert-danger py-2 mb-2">{generalErrors.map((e, i) => <div key={i}>{e.message}</div>)}</div>
            }
            {execution &&
                <div className="alert alert-light border py-2 mb-2 d-flex align-items-center gap-2 fs-13">
                    <ExecutionStatusBadge status={execution.status} />
                    <span>{t('workflow:operationsCount', { count: execution.operations })}</span>
                    {execution.error && <span className="text-danger">{execution.error.moduleId ? `${execution.error.moduleId}: ` : ''}{execution.error.message}</span>}
                    {execution.status === 'waiting' && <span className="text-primary">{t('workflow:waitingFor_' + (execution.waitingFor?.type || 'other'), { defaultValue: execution.waitingFor?.type })}</span>}
                    <Link className="ms-auto" to={route.AUTOMATION_RUN.replace(':uuid', execution.uuid)}>{t('workflow:runDetails')}</Link>
                    <button type="button" className="btn-close btn-sm" onClick={() => setExecution(null)} />
                </div>
            }

            {/* Canvas: the theme's card colour, and React Flow's own controls and grid in the same mode */}
            <div className="card flex-grow-1 mb-0 position-relative">
                <ReactFlow
                    colorMode={layoutMode}
                    style={{ backgroundColor: 'transparent' }}
                    nodes={nodes}
                    edges={graph.edges}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    onNodesChange={onNodesChange}
                    onNodeDragStop={onNodeDragStop}
                    onNodeClick={onNodeClick}
                    nodesDraggable={!readOnly}
                    nodesConnectable={false}
                    fitView
                    fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
                    minZoom={0.2}
                    proOptions={{ hideAttribution: true }}
                >
                    <Background gap={24} />
                    <Controls showInteractive={false} />
                </ReactFlow>
            </div>

            <ModulePanel
                blueprint={blueprint}
                moduleId={selectedModule ? selectedModule.id : null}
                catalog={catalog}
                samples={samples}
                problems={selectedModule ? problemsOf(selectedModule.id) : []}
                readOnly={readOnly}
                onChange={(module) => change(updateModule(blueprint, module.id, () => module))}
                onRemove={(id) => { change(removeModule(blueprint, id)); setSelected(null); }}
                onAddErrorHandler={(id) => { setSelected(null); setPicker({ kind: 'onerror', moduleId: id }); }}
                onAddRoute={(id) => change(addRoute(blueprint, id))}
                onClose={() => setSelected(null)}
            />
            <ModulePicker
                placement={picker}
                catalog={catalog}
                isFirst={picker?.kind === 'start'}
                onPick={addModule}
                onClose={() => setPicker(null)}
            />
            <FilterEditor
                moduleId={filterOf}
                filter={filterOf !== null ? findModule(blueprint, filterOf)?.filter : null}
                readOnly={readOnly}
                onSave={(filter: WorkflowFilter | null) => {
                    if (filterOf !== null) {
                        change(updateModule(blueprint, filterOf, (m) => {
                            const { filter: _old, ...rest } = m;

                            return filter ? { ...rest, filter } : (rest as BlueprintModule);
                        }));
                    }
                    setFilterOf(null);
                }}
                onClose={() => setFilterOf(null)}
            />
            <ScheduleModal
                show={modal === 'schedule'}
                schedule={scenario.schedule}
                isInstant={scenario.isInstant}
                minIntervalMinutes={scenario.minIntervalMinutes || 15}
                readOnly={readOnly}
                onSave={async (schedule: WorkflowSchedule) => {
                    const saved = await save({ schedule });
                    if (saved) {
                        setModal(null);
                    }
                }}
                onClose={() => setModal(null)}
            />
            <NameModal
                show={templateNaming}
                title={t('workflow:saveAsTemplate')}
                label={t('workflow:templateName')}
                initial={name}
                onSave={saveAsTemplate}
                onClose={() => setTemplateNaming(false)}
            />
            <SettingsModal
                show={modal === 'settings'}
                settings={scenario.settings || {}}
                description={scenario.description || ''}
                readOnly={readOnly}
                onSave={async (settings: WorkflowSettings, description: string) => {
                    const saved = await save({ settings, description });
                    if (saved) {
                        setModal(null);
                    }
                }}
                onClose={() => setModal(null)}
            />
            <VersionsModal
                show={modal === 'versions'}
                scenarioUuid={isNew ? null : scenario.uuid}
                currentVersion={scenario.version}
                readOnly={readOnly}
                onRestore={restore}
                onClose={() => setModal(null)}
            />
            <BundlesModal
                moduleLabel={bundlesOf !== null ? `${bundlesOf}. ${catalog.byKey[findModule(blueprint, bundlesOf)?.module || '']?.label || ''}` : ''}
                steps={bundlesOf !== null ? runByModule[bundlesOf]?.steps || [] : null}
                onClose={() => setBundlesOf(null)}
            />
        </div>
    );
};

/** Make-style scenario editor: /automation/scenario/:uuid ("new" for a blank one). */
const ScenarioEditor = () => (
    <ReactFlowProvider>
        <MappingProvider>
            <ScenarioEditorInner />
        </MappingProvider>
    </ReactFlowProvider>
);

export default ScenarioEditor;
